import {
  Box,
  Divider,
  FormControl,
  Input,
  MenuItem,
  Modal,
  Select,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import PrimaryButton from "../../../../components/buttons/PrimaryButton";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import LoadingIcon from "../../../../components/LoadingIcon";
import { Toast } from "../../../../components/Toast";

const DeleteControlModal = ({ open, handleClose }) => {
  const colors = useColors();
  const { getCustomersWithSecretStatus, deleteControl } = useAPI();

  const [customerId, setCustomerId] = useState("");
  const [customerList, setCustomerList] = useState([]);
  const [controlId, setControlId] = useState("");
  const [onDelete, setOnDelete] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [manualCustomerId, setManualCustomerId] = useState("");

  useEffect(() => {
    if (open) {
      getCustomersWithSecretStatus()
        .then((response) => {
          setCustomerList(response.data);
        })
        .catch((error) => {
          Toast.error("Failed to fetch customer list");
        });
    }
  }, [open]);

  useEffect(() => {
    if (!open) {
      setCustomerId("");
      setControlId("");
      setConfirmText("");
      setIsConfirmed(false);
      setIsOtherSelected(false);
      setManualCustomerId("");
    }
  }, [open]);

  useEffect(() => {
    setIsConfirmed(confirmText === "delete");
  }, [confirmText]);

  const handleCancel = () => {
    handleClose();
  };

  const handleSelectChange = (event) => {
    const value = event.target.value;
    if (value === "other") {
      setIsOtherSelected(true);
      setCustomerId("");
    } else {
      setIsOtherSelected(false);
      setManualCustomerId("");
      setCustomerId(value);
    }
  };

  const handleManualInputChange = (event) => {
    setManualCustomerId(event.target.value);
  };

  const handleControlIdChange = (event) => {
    const value = event.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setControlId(value);
    }
  };

  const handleDelete = () => {
    const idToDelete = isOtherSelected ? manualCustomerId : customerId;
    if (!idToDelete || !controlId || !isConfirmed) return;

    const controlIdNum = parseInt(controlId, 10);

    setOnDelete(true);
    deleteControl(idToDelete, controlIdNum)
      .then(() => {
        Toast.success(`Control ${controlIdNum} deleted successfully`);
        handleClose();
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(
            error.response.data ||
              `Failed to delete control: access denied or insufficient permissions`
          );
        } else if (error.message) {
          Toast.error(`Failed to delete control: ${error.message}`);
        } else {
          Toast.error(
            `Failed to delete control ${controlIdNum} for customer ${idToDelete}`
          );
        }
      })
      .finally(() => {
        setOnDelete(false);
      });
  };

  const customerIdExists = (id) => {
    return customerList.some((customer) => customer.customer_id === id);
  };

  const isValidCustomer = isOtherSelected
    ? manualCustomerId && !customerIdExists(manualCustomerId)
    : customerId && customerIdExists(customerId);

  const isValidControlId = controlId && /^\d+$/.test(controlId);

  const customerOptions = customerList.map((customer) => (
    <MenuItem
      key={customer.customer_id}
      value={customer.customer_id}
      sx={{
        color: customer.has_secret ? "inherit" : colors.grey_60percent,
      }}
    >
      {customer.customer_id}
      {!customer.has_secret && (
        <Typography
          component="span"
          sx={{
            ml: 1,
            color: colors.grey_60percent,
            fontSize: "11px",
            fontStyle: "italic",
          }}
        >
          (no secret found)
        </Typography>
      )}
    </MenuItem>
  ));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
    >
      <Box
        sx={{
          width: 500,
          bgcolor: "background.paper",
          margin: "auto",
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          boxShadow: 24,
          overflowY: "auto",
          borderRadius: "10px",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            overflowY: "auto",
          }}
        >
          <Box
            sx={{
              textAlign: "center",
              position: "sticky",
              top: 0,
              zIndex: 1,
              backgroundColor: colors.common_white,
              pt: "5%",
            }}
            id="modal-modal-title"
          >
            <Typography variant="subtitle1" gutterBottom>
              Delete Control
            </Typography>
            <Typography
              variant="body1"
              gutterBottom
              sx={{
                marginTop: "4px",
                color: colors.error_main,
                fontWeight: "bold",
              }}
            >
              DANGER: This operation is irreversible
            </Typography>
          </Box>
          <Box sx={{ padding: "32px" }}>
            <FormControl variant="standard" fullWidth>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Customer ID
              </Typography>
              <Select
                value={isOtherSelected ? "other" : customerId}
                onChange={handleSelectChange}
                displayEmpty
                sx={{
                  "& .MuiSelect-select": {
                    fontSize: "12px",
                  },
                  marginBottom: "8px",
                }}
              >
                <MenuItem value="" disabled>
                  Select a customer ID
                </MenuItem>
                {customerOptions}
                <MenuItem value="other">Other (manual entry)</MenuItem>
              </Select>

              {isOtherSelected && (
                <Input
                  placeholder="Enter customer ID manually"
                  value={manualCustomerId}
                  onChange={handleManualInputChange}
                  disableUnderline
                  sx={{
                    "& .MuiInput-input": {
                      borderBottom: `1px solid ${
                        manualCustomerId
                          ? colors.grey_32percent
                          : colors.error_main
                      }`,
                      fontSize: "12px",
                    },
                    marginTop: "8px",
                  }}
                />
              )}

              {isOtherSelected &&
                manualCustomerId &&
                customerIdExists(manualCustomerId) && (
                  <Typography
                    variant="caption"
                    sx={{ color: colors.error_main }}
                  >
                    This customer ID exists in the dropdown - please select it
                    from there
                  </Typography>
                )}
            </FormControl>

            <Divider style={{ marginBottom: "16px", marginTop: "16px" }} />

            <FormControl variant="standard" fullWidth sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontSize={"14px"}>
                Control ID
              </Typography>
              <Input
                placeholder="Enter control ID (numeric)"
                value={controlId}
                onChange={handleControlIdChange}
                disableUnderline
                sx={{
                  "& .MuiInput-input": {
                    borderBottom: `1px solid ${
                      isValidControlId
                        ? colors.grey_32percent
                        : colors.error_main
                    }`,
                    fontSize: "12px",
                  },
                }}
              />
            </FormControl>

            {isValidCustomer && isValidControlId && (
              <>
                <FormControl variant="standard" fullWidth sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" fontSize={"14px"}>
                    Confirm by typing: "delete"
                  </Typography>
                  <Input
                    placeholder="Type confirmation text"
                    value={confirmText}
                    onChange={(event) => setConfirmText(event.target.value)}
                    disableUnderline
                    sx={{
                      "& .MuiInput-input": {
                        borderBottom: `1px solid ${
                          isConfirmed ? colors.success_main : colors.error_main
                        }`,
                        fontSize: "12px",
                      },
                    }}
                  />
                </FormControl>
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: colors.error_light,
                    borderLeft: `4px solid ${colors.error_main}`,
                    borderRadius: "4px",
                  }}
                >
                  <Typography variant="caption" color={colors.error_dark}>
                    WARNING: This will permanently delete the control and all
                    related misconfigurations. This operation cannot be undone.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 2,
              alignItems: "center",
              marginBottom: "24px",
            }}
          >
            <SecondaryButton
              onClick={handleCancel}
              sx={{
                color: colors.primary_main,
                borderRadius: "10px",
                border: "none",
                height: "30px",
                fontSize: "12px",
              }}
            >
              Cancel
            </SecondaryButton>
            {onDelete ? (
              <LoadingIcon />
            ) : (
              <PrimaryButton
                onClick={handleDelete}
                disabled={
                  !isConfirmed ||
                  !isValidCustomer ||
                  !isValidControlId ||
                  onDelete
                }
                style={{
                  color: colors.common_white,
                  backgroundColor:
                    isConfirmed &&
                    isValidCustomer &&
                    isValidControlId &&
                    !onDelete
                      ? colors.error_main
                      : colors.grey_40percent,
                  borderRadius: "10px",
                  border: "none",
                  cursor:
                    isConfirmed &&
                    isValidCustomer &&
                    isValidControlId &&
                    !onDelete
                      ? "pointer"
                      : "not-allowed",
                }}
                size="small"
              >
                Delete Control
              </PrimaryButton>
            )}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default DeleteControlModal;
